import React, { useState } from 'react';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, Mail, Phone, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCMSContent } from '../hooks/useCMSContent';
import { useBrandSettings } from '../contexts/BrandSettingsContext';


const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { content: cmsContent, loading } = useCMSContent('contact-page');
  const { contact } = useBrandSettings();

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const infoSection = cmsContent?.infoSection;
  const officeHours = cmsContent?.officeHours;
  const form = cmsContent?.form;
  const subjectOptions = cmsContent?.subjectOptions;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormLoading(true);

    setTimeout(() => {
      setFormLoading(false);
      setSubmitted(true);
      if (form?.successToast) {
        toast.success(form.successToast);
      }
    }, 1000);
  };

  // Show loading state
  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Show error if CMS content is not configured
  if (!hero || !form) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Contact page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/contact-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero, form
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero Section */}
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {hero.subtitle}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="py-20">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Info Column */}
            <div>
              {infoSection?.title && (
                <h2 className="text-2xl font-bold mb-8">{infoSection.title}</h2>
              )}
              <div className="space-y-6">
                {/* Email - from brand settings */}
                {contact?.email && (
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{infoSection?.emailLabel}</h3>
                      <a 
                        href={`mailto:${contact.email}`}
                        className="text-muted-foreground text-sm hover:text-primary transition-colors"
                      >
                        {contact.email}
                      </a>
                    </div>
                  </div>
                )}

                {/* Phone - from brand settings */}
                {contact?.phones?.length > 0 && (
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{infoSection?.phoneLabel}</h3>
                      <div className="space-y-1">
                        {contact.phones.map((phone, idx) => (
                          <a 
                            key={idx}
                            href={`tel:${phone.number.replace(/\s/g, '')}`}
                            className="block text-muted-foreground text-sm hover:text-primary transition-colors"
                          >
                            {phone.label && <span className="text-xs text-gray-400 mr-2">({phone.label})</span>}
                            {phone.number}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Address - from brand settings */}
                {contact?.address && (
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{infoSection?.addressLabel}</h3>
                      <p className="text-muted-foreground text-sm">
                        {contact.address.formatted || `${contact.address.street}, ${contact.address.area}, ${contact.address.city}, ${contact.address.province}, ${contact.address.country} ${contact.address.postalCode}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Office Hours - from CMS */}
              {officeHours && (
                <div className="mt-12 p-6 bg-muted/50 rounded-xl">
                  {officeHours.title && (
                    <h3 className="font-semibold mb-3">{officeHours.title}</h3>
                  )}
                  {officeHours.hours && (
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {officeHours.hours.map((hour, idx) => (
                        <p key={idx}>{hour}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Form Column */}
            <div>
              {submitted ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl shadow-sm">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  {form.successTitle && (
                    <h2 className="text-2xl font-bold mb-4">{form.successTitle}</h2>
                  )}
                  {form.successMessage && (
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      {form.successMessage}
                    </p>
                  )}
                  {form.sendAnotherButton && (
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      {form.sendAnotherButton}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
                  {form?.title && (
                    <h2 className="text-xl font-bold mb-6">{form.title}</h2>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">{form.firstNameLabel} *</Label>
                        <Input id="firstName" placeholder={form.firstNamePlaceholder} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">{form.lastNameLabel} *</Label>
                        <Input id="lastName" placeholder={form.lastNamePlaceholder} required />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{form.emailLabel} *</Label>
                      <Input id="email" type="email" placeholder={form.emailPlaceholder} required />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">{form.companyLabel}</Label>
                      <Input id="company" placeholder={form.companyPlaceholder} />
                    </div>

                    {subjectOptions && subjectOptions.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="subject">{form.subjectLabel} *</Label>
                        <select
                          id="subject"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          required
                        >
                          <option value="">{form.subjectPlaceholder}</option>
                          {subjectOptions.map((option, idx) => (
                            <option key={idx} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="message">{form.messageLabel} *</Label>
                      <Textarea
                        id="message"
                        placeholder={form.messagePlaceholder}
                        className="min-h-[120px]"
                        required
                      />
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={formLoading}>
                      {formLoading ? form.sendingText : form.submitButton}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default ContactPage;
